import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, List, Phone, X } from "@phosphor-icons/react";
import { COMPANY, NAV_LINKS } from "@/lib/content";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <span className="w-9 h-9 bg-ink text-bone flex items-center justify-center font-cabinet font-800 text-lg leading-none transition-colors duration-300 group-hover:bg-signal">
      A
    </span>
    <span className="leading-tight">
      <span className="block font-cabinet font-bold tracking-tight text-base">Al Maler Mester</span>
      <span className="block text-[10px] uppercase tracking-[0.25em] text-soot">ApS · Kun erhverv</span>
    </span>
  </Link>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-bone/85 backdrop-blur-xl border-ink/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Hovednavigation">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-signal ${
                  isActive ? "text-signal" : "text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-call-button"
            className="hidden md:flex items-center gap-2 text-sm font-semibold px-4 py-2.5 border border-ink/20 hover:border-ink transition-colors duration-300"
          >
            <Phone size={16} weight="bold" />
            {COMPANY.phoneDisplay}
          </a>
          <Link
            to="/kontakt"
            data-testid="nav-quote-button"
            className="hidden sm:flex items-center gap-2 bg-signal text-bone text-sm font-semibold px-5 py-2.5 group hover:bg-ink transition-colors duration-300"
          >
            Indhent tilbud
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-11 h-11 flex items-center justify-center border border-ink/20"
            aria-label="Åbn menu"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-bone border-t border-ink/10 overflow-y-auto"
          >
            <nav className="px-5 py-10 flex flex-col gap-1" aria-label="Mobilnavigation">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    className={({ isActive }) =>
                      `block font-cabinet text-3xl font-bold tracking-tight py-3 border-b border-ink/10 ${
                        isActive ? "text-signal" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="flex flex-col gap-3 mt-8">
                <Link
                  to="/kontakt"
                  data-testid="mobile-quote-button"
                  className="flex items-center justify-center gap-2 bg-signal text-bone font-semibold px-6 py-4"
                >
                  Indhent tilbud <ArrowUpRight size={18} weight="bold" />
                </Link>
                <a
                  href={COMPANY.phoneHref}
                  data-testid="mobile-call-button"
                  className="flex items-center justify-center gap-2 border border-ink font-semibold px-6 py-4"
                >
                  <Phone size={18} weight="bold" /> Ring {COMPANY.phoneDisplay}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
